import { getExcelForShinseisha, getExcelForShoninsha } from "@/api/shinsei.api";
import { Shinchoku, Status } from "@/constants/common.constant";
import { ErrorMsg } from "@/constants/message.constant";
import { Role } from "@/constants/role.constant";
import { baseQueryShinsei, QueryShinsei } from "@/interfaces/shinsei.interface";
import { useUserStore } from "@/store";
import { dateFormat } from "@/utils/date";
import dayjs from "dayjs";
import { ElMessage } from "element-plus";
import { reactive, ref } from "vue";
import {
  Buttons,
  ExcelForShinseisha,
  ExcelForShoninsha,
  Prop2LabelMap,
} from "./config";

// ステータスに色付ける
export function statusFilter(status: string) {
  const statusType: Record<string, string> = {
    申請前: "default",
    申請中: "success",
    承認: "info",
    保留: "danger",
    期限切: "warning",
  };
  return statusType[status];
}

// excel出力
export function export2Excel(queryForm: QueryShinsei) {
  const exportLoading = ref(false);
  const handleExport2excel = async () => {
    try {
      exportLoading.value = true;
      const query = {
        startDate: queryForm.startDate,
        statusCd: queryForm.statusCd,
        kigyouShinchokuShinseiCd: queryForm.kigyouShinchokuShinseiCd,
        kigyouShinchokuShoninCd: queryForm.kigyouShinchokuShoninCd,
        kaishaNm: queryForm.kaishaNm,
        groupNm: queryForm.groupNm,
        kjnNm: queryForm.kjnNm,
        bushoNm: queryForm.bushoNm,
        itakuKaishaNm: queryForm.itakuKaishaNm,
        saiItakuKaishaNm: queryForm.saiItakuKaishaNm,
        riyoMokuteki: queryForm.riyoMokuteki,
        endDate: queryForm.endDate,
      };
      if (useUserStore().isShinseisha) {
        await generateExcelForShinseisha(query);
      } else {
        await generateExcelForShoninsha(query);
      }
    } catch (error) {
      ElMessage.error(ErrorMsg.ERR_EXPORT_FAIL);
    } finally {
      exportLoading.value = false;
    }
  };
  return {
    exportLoading,
    handleExport2excel,
  };
}
//申請者向けexcel作成
async function generateExcelForShinseisha(query: baseQueryShinsei) {
  const { data } = await getExcelForShinseisha(query);
  const { listData } = data.result;
  const list: ExcelForShinseisha[] = [];
  for (let i = 0; i < listData.length; i++) {
    list.push({
      shinseiId: listData[i].shinsei_id,
      statusNm: listData[i].status.status, //ステータス
      kigyonaiShinchokuNmShinsei:
        listData[i].kigyonai_shinchoku_shinsei?.kigyonai_shinchoku, //企業内進捗_申請
      kaishaNm: listData[i].toroku_shinseisha.kaishaGroup?.kaisha.kaisha_nm, //会社名
      kaishaGroupNm: listData[i].toroku_shinseisha.kaishaGroup?.kaisha_group_nm, //会社グループ名
      torokuKjnNm: listData[i].toroku_shinseisha?.kjn_nm, //登録_個人名
      torokuBushoNm: listData[i].toroku_shinseisha?.busho_nm, //登録_部署名
      kanriKjnNm: listData[i].kanri_shinseisha?.kjn_nm, //管理_個人名
      kanriBushoNm: listData[i].kanri_shinseisha?.busho_nm, //管理_部署名
      shoninTorokuTantoNm: listData[i].toroku_shoninsha?.tanto_nm, //ULT承認_登録_担当者名
      shoninKanriTantoNm: listData[i].kanri_shoninsha?.tanto_nm, //ULT承認_管理_担当者名
      itakusakiKaishaNm: listData[i].ss_itakusaki_kaisha_nm, //申請_委託先_会社名
      itakusakiJusho: listData[i].ss_itakusaki_jusho, //申請_委託先_住所
      saiItakusakiKaishaNm: listData[i].ss_saiitakusaki_kaisha_nm, //申請_再委託先_会社名
      saiItakusakiJusho: listData[i].ss_saiitakusaki_jusho, //申請_再委託先_住所
      riyoBasho: listData[i].ss_riyo_basho, //申請_利用場所
      kikanKaishibi: listData[i].ss_kikan_kaishibi, //申請_期間_開始日
      kikanShuryobi: listData[i].ss_kikan_shuryobi, //申請_期間_終了日
      riyomokuteki: listData[i].ss_riyomokuteki, //申請_利用目的
      riyohoho: listData[i].ss_riyohoho, //申請_利用方法
      riyohaniDcfFlg: listData[i].ss_riyohani_dcf_flg, //申請_利用範囲_DCF_フラグ
      riyohaniDsfFlg: listData[i].ss_riyohani_dsf_flg, //申請_利用範囲_DSF_フラグ
      riyohaniDnfFlg: listData[i].ss_riyohani_dnf_flg, //申請_利用範囲_DNF_フラグ
      riyohaniEcfFlg: listData[i].ss_riyohani_ecf_flg, //申請_利用範囲_ECF_フラグ
      riyohaniPcfFlg: listData[i].ss_riyohani_pcf_flg, //申請_利用範囲_PCF_フラグ
      riyohaniSonohokaFlg: listData[i].ss_riyohani_sonohoka_flg, //申請_利用範囲_その他_フラグ
      riyohaniSonohokaText: listData[i].ss_riyohani_sonohoka_text, //申請_利用範囲_その他_テキスト
      riyokomokuKjnCdFlg: listData[i].ss_riyokomoku_kjncd_flg, //申請_利用項目_個人コード_フラグ
      riyokomokuKjnNmFlg: listData[i].ss_riyokomoku_kjnnm_flg, //申請_利用項目_個人名_フラグ
      riyokomokuShiCdFlg: listData[i].ss_riyokomoku_shicd_flg, //申請_利用項目_施設コード_フラグ
      riyokomokuShiNmFlg: listData[i].ss_riyokomoku_shinm_flg, //申請_利用項目_施設名_フラグ
      riyokomokuShiJushoFlg: listData[i].ss_riyokomoku_shijusho_flg, //申請_利用項目_施設住所_フラグ
      riyokomokuShiTelFlg: listData[i].ss_riyokomoku_shitel_flg, //申請_利用項目_施設電話番号_フラグ
      riyokomokuSrkmFlg: listData[i].ss_riyokomoku_srkm_flg, //申請_利用項目_診療科目_フラグ
      riyokomokuSzbkFlg: listData[i].ss_riyokomoku_szbk_flg, //申請_利用項目_所属部科_フラグ
      riyokomokuSonohokaFlg: listData[i].ss_riyokomoku_sonohoka_flg, //申請_利用項目_その他_フラグ
      riyokomokuSonohokaText: listData[i].ss_riyokomoku_sonohoka_text, //申請_利用項目_その他_テキスト
      shinseiComment: listData[i].shinsei_comment, //申請_コメント
      shoninComment: listData[i].shonin_comment, //申請_コメント
      torokuDate: dateFormat(listData[i].toroku_date),
      koshinDate: dateFormat(listData[i].koshin_date),
    });
  }
  //出力処理
  //出力データセット
  const { aoaToSheetXlsx } = await import("@/utils/export2Excel"); //lazy load
  aoaToSheetXlsx({
    data: list,
    headerMapper: Prop2LabelMap,
    filename: `${dayjs().format("YYYYMMDD")}_許諾申請リスト.xlsx`,
  });
}
//承認者向けexcel作成
async function generateExcelForShoninsha(query: baseQueryShinsei) {
  const { data } = await getExcelForShoninsha(query);
  const { listData } = data.result;
  const list: ExcelForShoninsha[] = [];
  for (let i = 0; i < listData.length; i++) {
    list.push({
      shinseiId: listData[i].shinsei_id,
      statusCd: listData[i].status.status_cd,
      statusNm: listData[i].status.status, //ステータス
      kigyonaiShinchokuCdShinsei:
        listData[i].kigyonai_shinchoku_shinsei?.kigyonai_shinchoku_cd, //企業内進捗コード_申請
      kigyonaiShinchokuNmShinsei:
        listData[i].kigyonai_shinchoku_shinsei?.kigyonai_shinchoku, //企業内進捗_申請
      kigyonaiShinchokuCdShonin:
        listData[i].kigyonai_shinchoku_shonin?.kigyonai_shinchoku_cd, //企業内進捗コード_承認
      kigyonaiShinchokuNmShonin:
        listData[i].kigyonai_shinchoku_shonin?.kigyonai_shinchoku, //企業内進捗_承認
      gyoushuCd:
        listData[i].toroku_shinseisha.kaishaGroup?.kaisha.gyoushu
          .gyoushu_bunrui_cd, //業種分類コード
      gyoushuNm:
        listData[i].toroku_shinseisha.kaishaGroup?.kaisha.gyoushu
          .gyoushu_bunrui_nm, //業種分類名
      kaishaCd: listData[i].toroku_shinseisha.kaishaGroup?.kaisha.kaisha_cd, //会社コード
      kaishaNm: listData[i].toroku_shinseisha.kaishaGroup?.kaisha.kaisha_nm, //会社名
      kaishaGroupId: listData[i].toroku_shinseisha.kaishaGroup?.kaisha_group_id, //会社グループID
      kaishaGroupNm: listData[i].toroku_shinseisha.kaishaGroup?.kaisha_group_nm, //会社グループ名
      torokuAccountId: listData[i].toroku_shinseisha?.account.akaunto_id, //登録_アカウントID
      torokuKjnNm: listData[i].toroku_shinseisha?.kjn_nm, //登録_個人名
      torokuBushoNm: listData[i].toroku_shinseisha?.busho_nm, //登録_部署名
      kanriAccountId: listData[i].kanri_shinseisha?.account.akaunto_id, //管理_アカウントID
      kanriKjnNm: listData[i].kanri_shinseisha?.kjn_nm, //管理_個人名
      kanriBushoNm: listData[i].kanri_shinseisha?.busho_nm, //管理_部署名
      shoninTorokuAccountId: listData[i].toroku_shoninsha?.account.akaunto_id, //ULT承認_登録_アカウントID
      shoninTorokuTantoNm: listData[i].toroku_shoninsha?.tanto_nm, //ULT承認_登録_担当者名
      shoninKanriAccountId: listData[i].kanri_shoninsha?.account.akaunto_id, //ULT承認_管理_アカウントID
      shoninKanriTantoNm: listData[i].kanri_shoninsha?.tanto_nm, //ULT承認_管理_担当者名
      itakusakiKaishaNm: listData[i].ss_itakusaki_kaisha_nm, //申請_委託先_会社名
      itakusakiJusho: listData[i].ss_itakusaki_jusho, //申請_委託先_住所
      saiItakusakiKaishaNm: listData[i].ss_saiitakusaki_kaisha_nm, //申請_再委託先_会社名
      saiItakusakiJusho: listData[i].ss_saiitakusaki_jusho, //申請_再委託先_住所
      riyoBasho: listData[i].ss_riyo_basho, //申請_利用場所
      kikanKaishibi: listData[i].ss_kikan_kaishibi, //申請_期間_開始日
      kikanShuryobi: listData[i].ss_kikan_shuryobi, //申請_期間_終了日
      riyomokuteki: listData[i].ss_riyomokuteki, //申請_利用目的
      riyohoho: listData[i].ss_riyohoho, //申請_利用方法
      riyohaniDcfFlg: listData[i].ss_riyohani_dcf_flg, //申請_利用範囲_DCF_フラグ
      riyohaniDsfFlg: listData[i].ss_riyohani_dsf_flg, //申請_利用範囲_DSF_フラグ
      riyohaniDnfFlg: listData[i].ss_riyohani_dnf_flg, //申請_利用範囲_DNF_フラグ
      riyohaniEcfFlg: listData[i].ss_riyohani_ecf_flg, //申請_利用範囲_ECF_フラグ
      riyohaniPcfFlg: listData[i].ss_riyohani_pcf_flg, //申請_利用範囲_PCF_フラグ
      riyohaniSonohokaFlg: listData[i].ss_riyohani_sonohoka_flg, //申請_利用範囲_その他_フラグ
      riyohaniSonohokaText: listData[i].ss_riyohani_sonohoka_text, //申請_利用範囲_その他_テキスト
      riyokomokuKjnCdFlg: listData[i].ss_riyokomoku_kjncd_flg, //申請_利用項目_個人コード_フラグ
      riyokomokuKjnNmFlg: listData[i].ss_riyokomoku_kjnnm_flg, //申請_利用項目_個人名_フラグ
      riyokomokuShiCdFlg: listData[i].ss_riyokomoku_shicd_flg, //申請_利用項目_施設コード_フラグ
      riyokomokuShiNmFlg: listData[i].ss_riyokomoku_shinm_flg, //申請_利用項目_施設名_フラグ
      riyokomokuShiJushoFlg: listData[i].ss_riyokomoku_shijusho_flg, //申請_利用項目_施設住所_フラグ
      riyokomokuShiTelFlg: listData[i].ss_riyokomoku_shitel_flg, //申請_利用項目_施設電話番号_フラグ
      riyokomokuSrkmFlg: listData[i].ss_riyokomoku_srkm_flg, //申請_利用項目_診療科目_フラグ
      riyokomokuSzbkFlg: listData[i].ss_riyokomoku_szbk_flg, //申請_利用項目_所属部科_フラグ
      riyokomokuSonohokaFlg: listData[i].ss_riyokomoku_sonohoka_flg, //申請_利用項目_その他_フラグ
      riyokomokuSonohokaText: listData[i].ss_riyokomoku_sonohoka_text, //申請_利用項目_その他_テキスト
      shinseiComment: listData[i].shinsei_comment, //申請_コメント
      shoninComment: listData[i].shonin_comment, //申請_コメント
      shinseiBunruiCd: listData[i].shinsei_bunrui?.shinsei_bunrui_cd, //申請分類コード
      shinseiBunruiNm: listData[i].shinsei_bunrui?.shinsei_bunrui_nm, //申請分類
      torokuDate: dateFormat(listData[i].toroku_date),
      koshinDate: dateFormat(listData[i].koshin_date),
      shinseiDate: dateFormat(listData[i].shinsei_date),
      shoninDate:
        listData[i].shonin_date && dateFormat(listData[i].shonin_date),
    });
  }
  const { aoaToSheetXlsx } = await import("@/utils/export2Excel"); //lazy load
  aoaToSheetXlsx({
    data: list,
    headerMapper: Prop2LabelMap,
    filename: `${dayjs().format("YYYYMMDD")}_許諾申請リスト.xlsx`,
  });
}

export const generateButtons = () => {
  const pattern1 = () => {
    displayButtons.saveBtn = true;
    displayButtons.deleteBtn = true;
    displayButtons.copyBtn = true;
    displayButtons.kariShinseiBtn = true;
  };
  const pattern2 = () => {
    displayButtons.kariShoninBtn = true;
    displayButtons.kariHoryuBtn = true;
  };
  const pattern3 = () => {
    displayButtons.deleteBtn = true;
  };
  const pattern4 = () => {
    displayButtons.copyBtn = true;
  };
  const displayButtons = reactive({
    //申請者用button
    shinseiBtn: false,
    kariShinseiBtn: false,
    kariShinseiOkBtn: false,
    copyBtn: false,
    saveBtn: false,
    kariShinseiNgBtn: false,

    //承認者用button
    shoninBtn: false,
    kariShoninBtn: false,
    kariShoninOkBtn: false,
    horyuBtn: false,
    kariHoryuBtn: false,
    kariHoryuOkBtn: false,
    kariShoninNgBtn: false,
    kariHoryuNgBtn: false,

    //共通button
    deleteBtn: false,
  } as Buttons);
  const displayPatterns = new Map([
    [
      {
        status: "",
        shinchoku: "",
        role: Role.ShinseishaIppan,
      },
      () => {
        displayButtons.kariShinseiBtn = true;
        displayButtons.saveBtn = true;
      },
    ],
    [
      {
        status: "",
        shinchoku: "",
        role: Role.ShinseishaKanri,
      },
      () => {
        displayButtons.shinseiBtn = true;
        displayButtons.saveBtn = true;
      },
    ],
    [
      {
        status: Status.申請前,
        shinchoku: Shinchoku.保存,
        role: Role.ShinseishaIppan,
        isTorokusha: true,
      },
      () => {
        displayButtons.saveBtn = true;
        displayButtons.deleteBtn = true;
        displayButtons.kariShinseiBtn = true;
      },
    ],
    [
      {
        status: Status.申請前,
        shinchoku: Shinchoku.保存,
        role: Role.ShinseishaIppan,
        isSameGroup: true,
      },
      pattern4, //複写表示
    ],
    [
      {
        status: Status.申請前,
        shinchoku: Shinchoku.保存,
        role: Role.ShinseishaKanri,
        isTorokusha: true,
      },
      () => {
        displayButtons.saveBtn = true;
        displayButtons.deleteBtn = true;
        displayButtons.shinseiBtn = true;
      },
    ],
    [
      {
        status: Status.申請前,
        shinchoku: Shinchoku.保存,
        role: Role.ShinseishaKanri,
        isSameGroup: true,
      },
      pattern4, //複写表示
    ],
    [
      {
        status: Status.申請前,
        shinchoku: Shinchoku.仮申請,
        role: Role.ShinseishaKanri,
        isSameGroup: true,
      },
      () => {
        displayButtons.kariShinseiOkBtn = true;
        displayButtons.kariShinseiNgBtn = true;
        displayButtons.saveBtn = true;
      },
    ],
    [
      {
        status: Status.申請前,
        shinchoku: Shinchoku.取消,
        role: Role.ShinseishaIppan,
        isTorokusha: true,
      },
      pattern1, //複写、一時保存、削除、仮申請表示
    ],
    [
      {
        status: Status.申請中,
        shinchoku: "",
        role: Role.ShoninshaIppan,
      },
      pattern2, //仮承認、仮保留表示
    ],
    [
      {
        status: Status.申請中,
        shinchoku: "",
        role: Role.ShoninshaKanri,
      },
      () => {
        displayButtons.shoninBtn = true;
        displayButtons.horyuBtn = true;
        displayButtons.deleteBtn = true;
      },
    ],

    [
      {
        status: Status.申請中,
        shinchoku: Shinchoku.仮承認,
        role: Role.ShoninshaKanri,
      },
      () => {
        displayButtons.kariShoninOkBtn = true;
        displayButtons.kariShoninNgBtn = true;
        displayButtons.deleteBtn = true;
      },
    ],
    [
      {
        status: Status.申請中,
        shinchoku: Shinchoku.仮保留,
        role: Role.ShoninshaKanri,
      },
      () => {
        displayButtons.kariHoryuOkBtn = true;
        displayButtons.kariHoryuNgBtn = true;
        displayButtons.deleteBtn = true;
      },
    ],
    [
      {
        status: Status.申請中,
        shinchoku: Shinchoku.取消,
        role: Role.ShoninshaIppan,
      },
      pattern2, //仮承認、仮保留表示
    ],
    [
      {
        status: Status.申請中,
        shinchoku: Shinchoku.取消,
        role: Role.ShoninshaKanri,
      },
      pattern3, //削除表示
    ],
    [
      {
        status: Status.承認,
        shinchoku: "",
        role: Role.ShinseishaIppan,
        isSameGroup: true,
      },
      pattern4, //複写表示
    ],
    [
      {
        status: Status.承認,
        shinchoku: "",
        role: Role.ShinseishaKanri,
        isSameGroup: true,
      },
      pattern4, //複写表示
    ],
    [
      {
        status: Status.承認,
        shinchoku: "",
        role: Role.ShoninshaKanri,
      },
      pattern3, //削除表示
    ],
    [
      {
        status: Status.保留,
        shinchoku: "",
        role: Role.ShinseishaIppan,
        isTorokusha: true,
      },
      () => {
        displayButtons.saveBtn = true;
        displayButtons.kariShinseiBtn = true;
      },
    ],
    [
      {
        status: Status.保留,
        shinchoku: "",
        role: Role.ShinseishaIppan,
        isSameGroup: true,
      },
      pattern4, //複写表示
    ],
    [
      {
        status: Status.保留,
        shinchoku: "",
        role: Role.ShinseishaKanri,
        isTorokusha: true,
      },
      () => {
        displayButtons.saveBtn = true;
        displayButtons.shinseiBtn = true;
      },
    ],
    [
      {
        status: Status.保留,
        shinchoku: "",
        role: Role.ShinseishaKanri,
        isSameGroup: true,
      },
      pattern4, //複写表示
    ],
    [
      {
        status: Status.保留,
        shinchoku: "",
        role: Role.ShoninshaKanri,
      },
      pattern3, //削除表示
    ],
    [
      {
        status: Status.期限切,
        shinchoku: "",
        role: Role.ShinseishaIppan,
        isSameGroup: true,
      },
      pattern4, //複写表示
    ],
    [
      {
        status: Status.期限切,
        shinchoku: "",
        role: Role.ShinseishaKanri,
        isSameGroup: true,
      },
      pattern4, //複写表示
    ],
    [
      {
        status: Status.期限切,
        shinchoku: "",
        role: Role.ShoninshaKanri,
      },
      pattern3, //削除表示
    ],
  ]);

  return {
    displayPatterns,
    displayButtons,
  };
};
